import { useContext } from "react";
import { omit } from "lodash";
import { FormProvider, useForm } from "react-hook-form";
import { RouteComponentProps, useLocation } from "react-router";
import { RESOURCE_NAME } from "common/authorization";
import { buildLink } from "common/routing";
import {
  LogQuery,
  useLogQuery,
  useUpdateLogV2Mutation,
} from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import { track } from "../../utils/tracking";
import { AuthContext } from "../Authorization/AuthContext";
import { FlexRow } from "../Common/__styles__/Layout";
import { Button } from "../Common/Button";
import { ActionButton } from "./ActionButton";
import LogMutationForm, { LogMutationFormStructure } from "./LogMutationForm";
import { LOGS } from "./queries";

export type EditProps = Pick<RouteComponentProps<{ id: string }>, "match">;

const cleanupInformationRequest = (
  informationRequest: LogQuery["informationRequest"],
) => {
  return omit(informationRequest, ["__typename", "id", "property"]);
};

const Container = ({
  informationRequest,
}: {
  informationRequest: NonNullable<LogQuery["informationRequest"]>;
}) => {
  const location = useLocation<Maybe<{ prevLocation?: string }>>();
  const { authorized } = useContext(AuthContext);
  const { addSuccessToast, addErrorToast } = useStatusToasts();
  const form = useForm<LogMutationFormStructure>({
    defaultValues: cleanupInformationRequest(informationRequest),
  });
  const { handleSubmit, reset, formState } = form;

  const [update, { loading: updateLoading }] = useUpdateLogV2Mutation({
    onCompleted: data => {
      track("Updated Log");

      addSuccessToast("Updated successfully");

      reset(cleanupInformationRequest(data.updateInformationRequestV2));
    },
    onError: () => {
      addErrorToast("Could not update log");
    },
    refetchQueries: () => [{ query: LOGS }],
  });

  const canUpdateLog = authorized({
    resource: RESOURCE_NAME.INFORMATION_REQUEST,
    permission: "update",
  });

  const onSubmit = async (formData: LogMutationFormStructure) => {
    const data = omit(formData, "viaPublicSite", "sharedViaEmail", "address");
    await update({
      variables: {
        id: informationRequest.id,
        data,
      },
    });
  };

  const rightContainer = (
    <FlexRow gap="m">
      <Button
        size="small"
        styleVariant="primary"
        onClick={handleSubmit(onSubmit)}
        disabled={!canUpdateLog || !formState.isValid || !formState.isDirty}
        loading={updateLoading}
      >
        Update
      </Button>
      <ActionButton
        infoRequest={informationRequest}
        isDetailView={true}
        prevLocation={location.state?.prevLocation ?? buildLink("map")}
      />
    </FlexRow>
  );

  return (
    <FormProvider {...form}>
      <LogMutationForm
        data={informationRequest}
        subtitle="Edit log"
        rightContainer={rightContainer}
        prevLocation={location.state?.prevLocation}
      />
    </FormProvider>
  );
};

export default ({ match }: EditProps) => {
  const { data, loading, error } = useLogQuery({
    variables: { id: match.params.id },
  });

  if (!data?.informationRequest || error || loading) return null;

  const { informationRequest } = data;

  return <Container informationRequest={informationRequest} />;
};
